import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid} from '@components';
import {StyledH3, StyledSelect2, WhiteButton} from '@styled';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {Row, Col} from 'react-bootstrap';
// import {useHistory} from 'react-router-dom';

import {sagaActions} from './redux/saga';
import CorporateRequestUpdate from './CorporateRequestUpdate';

const statusFilterOptions = [
  {value: 'Pending', label: 'Pending'},
  {value: 'Rejected', label: 'Rejected'},
];

const RowActions = ({data, editAction}) => {
  return (
    <div>
      <WhiteButton onClick={() => editAction(data)} style={{margin: '.16em'}}>
        Approve / Reject
      </WhiteButton>
    </div>
  );
};

const CorporateRequests = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(
    (state) => state.helpdesk.corporateRequests.loading
  );
  const remoteData = useSelector((state) => state.helpdesk.corporateRequests);

  const [detailsRow, setRowDetails] = React.useState(null);

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortDir: 'ASC',
    sortOn: 'createdBy',
    filter: {status: 'Pending'},
  });

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir ||
      remoteData.filter !== pagination.filter
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize || pagination.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn || pagination.sortOn,
        sortDir: remoteData.sortDir || pagination.sortDir,
        filter: remoteData.filter || pagination.filter,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortDir,
    sortOn,
    filter = pagination.filter,
  }) => {
    dispatch({
      type: sagaActions.FETCH_CORPORATE_REQUESTS,
      payload: {
        values: {
          filter,
          search: '',
          page,
          pageSize,
          sortDir,
          sortOn,
        },
      },
    });
  };

  const handleShowRowDetails = React.useCallback((row) => {
    setRowDetails(row);
  }, []);

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} editAction={handleShowRowDetails} />;
  };

  const Columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        Header: '#',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
      },
      {
        accessor: 'name',
        Header: 'Corporate Name',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'status',
        Header: 'Status',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'executive.fullName',
        Header: 'Requested By',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'createdOn',
        Header: 'Requested On',
        tooltip: false,
        className: 'd-none d-lg-block',
        Cell: ({value}) => (value ? moment(value).fromNow() : ''),
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 50,
      },
    ],
    [pagination]
  );

  const renderDetailsModal = () => {
    return detailsRow ? (
      <CorporateRequestUpdate
        data={detailsRow}
        onHide={() => setRowDetails(null)}
      />
    ) : null;
  };
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(
    statusFilterOptions.find(
      (item) =>
        item.value === remoteData.filter.status || pagination.filter.status
    ) || statusFilterOptions[0]
  );
  const handleStatusFilterChange = (val) => {
    setSelectedStatusFilter(val);
  };

  useEffect(() => {
    if (
      selectedStatusFilter &&
      selectedStatusFilter.value !== remoteData.filter.status
    ) {
      fetchData({
        page: 1,
        pageSize: pagination.pageSize,
        sortDir: 'DESC',
        sortOn: 'createdBy',
        filter: {...pagination.filter, status: selectedStatusFilter.value},
      });
    }
  }, [selectedStatusFilter]);

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Row>
          <Col md={9}>
            <StyledH3>{t('owner.corporateRequests.pageHeading')}</StyledH3>
          </Col>
          <Col md={3}>
            <div className="d-flex flex-row justify-content-start align-items-center">
              <span className="pr-3">View List:</span>
              <StyledSelect2
                name="statusFilter"
                options={statusFilterOptions}
                value={selectedStatusFilter}
                onChange={handleStatusFilterChange}
              />
            </div>
          </Col>
        </Row>

        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
      {renderDetailsModal()}
    </>
  );
};

export default CorporateRequests;
